import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { isPdfFile, MAX_PDF_BYTES, fmtBytes } from "../../lib/subby";
import { Field, Input, NumberInput, Modal, PrimaryButton, QuietButton, Select } from "../UI";
import { DateInput } from "../DateInput";
import { SubcontractorDialog } from "./SubcontractorDialog";

const EMPTY = { subcontractor_id: "", trade_id: "", amount: "", quote_date: "", valid_until: "", reference: "", notes: "" };

export function PdfPicker({ label, required, file, onChange, testid, hint }) {
  return (
    <Field label={label} required={required} hint={hint}>
      <label className="flex items-center gap-3 h-9 px-3 rounded-md border border-dashed border-line bg-surface cursor-pointer hover:border-accent" style={{ fontSize: 12.5 }}>
        <Upload size={13} className="text-ink-3" />
        <span className={file ? "text-ink" : "text-ink-4"} data-testid={`${testid}-name`}>
          {file ? `${file.name} · ${fmtBytes(file.size)}` : "Choose a PDF (max 10 MB)"}
        </span>
        <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={testid}
          onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    </Field>
  );
}

export function checkPdf(file, label) {
  if (!file) return null;
  if (!isPdfFile(file)) return `${label} must be a PDF.`;
  if (file.size > MAX_PDF_BYTES) return `${label} is over the 10 MB limit.`;
  return null;
}

/** LT-37 — upload a new subby quote: details + original PDF (+ optional client copy). */
export function SubbyQuoteDialog({ open, onClose, tenderId, onSaved }) {
  const [subs, setSubs] = useState([]);
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newSubOpen, setNewSubOpen] = useState(false);

  const loadSubs = () => api.get("/subcontractors").then((r) => setSubs(r.data || [])).catch(() => {});
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setOriginal(null); setClient(null); setError(null);
    loadSubs();
    api.get("/labour-rates").then((r) => setTrades(r.data || [])).catch(() => {});
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const pickSub = (id) => {
    const s = subs.find((x) => x.id === id);
    setForm((f) => ({ ...f, subcontractor_id: id, trade_id: f.trade_id || (s?.trade_ids?.[0] || "") }));
  };
  // LT-39 — quick add: a subby created here is selected straight away.
  const onNewSub = (created) => {
    if (!created?.id) { loadSubs(); return; }
    setSubs((cur) => [...cur.filter((x) => x.id !== created.id), created].sort((a, b) => a.company_name.localeCompare(b.company_name)));
    setForm((f) => ({ ...f, subcontractor_id: created.id, trade_id: f.trade_id || (created.trade_ids?.[0] || "") }));
  };

  const save = async () => {
    const problem = !form.subcontractor_id ? "Pick a subcontractor."
      : form.amount === "" || Number.isNaN(Number(form.amount)) ? "Enter the quote amount."
      : !original ? "The original quote PDF is required."
      : checkPdf(original, "Original") || checkPdf(client, "Client copy");
    if (problem) { setError(problem); return; }
    setSaving(true); setError(null);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v !== "" && v !== null) fd.append(k, v); });
    fd.append("original", original);
    if (client) fd.append("client", client);
    try {
      const r = await api.post(`/tenders/${tenderId}/subby-quotes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Subby quote uploaded");
      onSaved?.(r.data); onClose();
    } catch (e) { setError(formatApiError(e, "Upload failed.")); }
    finally { setSaving(false); }
  };

  const subOptions = subs.map((s) => ({ value: s.id, label: s.company_name + (s.trade_names?.length ? ` · ${s.trade_names.join(", ")}` : "") }));
  const tradeOptions = [{ value: "", label: "— none —" }, ...trades.map((t) => ({ value: t.id, label: t.trade_name }))];

  return (
    <Modal open={open} onClose={onClose} title="Add subby quote" testid="subby-quote-dialog" maxWidth={640}
      footer={<>
        <QuietButton onClick={onClose} data-testid="subby-quote-dialog-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Upload} data-testid="subby-quote-dialog-save">Upload quote</PrimaryButton>
      </>}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2" data-testid="subby-quote-dialog-subcontractor">
          <Field label="Subcontractor" required hint={subs.length === 0 ? <span>Directory is empty — add the subcontractor with the button, or in the <Link to="/labour-rates?tab=directory" className="underline">Directory</Link>.</span> : "Not in the Directory yet? Add them without leaving this form."}>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0"><Select value={form.subcontractor_id} onChange={pickSub} options={subOptions} placeholder="Pick from the Directory…" /></div>
              <button type="button" onClick={() => setNewSubOpen(true)} data-testid="subby-quote-dialog-new-sub"
                className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-line bg-surface text-ink-2 hover:text-ink hover:border-accent flex-shrink-0" style={{ fontSize: 12.5 }}>
                <Plus size={13} /> New subcontractor
              </button>
            </div>
          </Field>
        </div>
        <div data-testid="subby-quote-dialog-trade">
          <Field label="Trade" hint="Defaults to the subby's first trade.">
            <Select value={form.trade_id} onChange={set("trade_id")} options={tradeOptions} placeholder="Trade…" />
          </Field>
        </div>
        <Field label="Quote amount (ex VAT)" required>
          <NumberInput value={form.amount} onChange={set("amount")} prefix="£" placeholder="0.00" data-testid="subby-quote-dialog-amount" />
        </Field>
        <Field label="Quote date" hint="Type DD/MM/YYYY or pick from the calendar.">
          <DateInput value={form.quote_date} onChange={set("quote_date")} testid="subby-quote-dialog-date" />
        </Field>
        <Field label="Valid until" hint="Type DD/MM/YYYY or pick from the calendar.">
          <DateInput value={form.valid_until} onChange={set("valid_until")} testid="subby-quote-dialog-valid" />
        </Field>
        <div className="col-span-2">
          <Field label="Subby's reference">
            <Input value={form.reference} onChange={set("reference")} placeholder="Their quote number" data-testid="subby-quote-dialog-reference" />
          </Field>
        </div>
        <div className="col-span-2">
          <PdfPicker label="Original quote (as received)" required file={original} onChange={setOriginal} testid="subby-quote-dialog-original"
            hint="Stored as revision 1 and never modified or replaced — later corrections are added as revisions." />
        </div>
        <div className="col-span-2">
          <PdfPicker label="Client-facing copy" file={client} onChange={setClient} testid="subby-quote-dialog-client"
            hint="Optional now — can be added later from the quote row." />
        </div>
        <div className="col-span-2">
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="subby-quote-dialog-notes"
              className="w-full px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y" style={{ fontSize: 13, lineHeight: "18px" }} />
          </Field>
        </div>
      </div>
      {error && <div className="mt-4 rounded-md border px-3 py-2" style={{ background: "#FBECEC", borderColor: "#E9C0C0", color: "#7A1F1F", fontSize: 12 }} data-testid="subby-quote-dialog-error">{error}</div>}
      <SubcontractorDialog open={newSubOpen} onClose={() => setNewSubOpen(false)} trades={trades} onSaved={onNewSub} />
    </Modal>
  );
}
